import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';

export const EXCLUSION_REASONS = Object.freeze({
  empty: 'No recorded value — field omitted from the letter',
  unknown:
    'Clinical status not established — no assertion made (Sapio null rule)'
});

function exclusionReason(state) {
  return EXCLUSION_REASONS[state] || `Field omitted (${state})`;
}

/**
 * Map a render `result` (from renderTemplate) + generation `meta` into the
 * payload accepted by POST /letter-template/generation.
 * @param {object} meta   { templateId, templateVersion, templateName, familyId, familyMemberId, letterId }
 * @param {object} result { resolutions, missing, errorTokens }
 */
export function buildGenerationPayload(meta = {}, result = {}) {
  const resolutions = Array.isArray(result.resolutions)
    ? result.resolutions
    : [];
  const missing = Array.isArray(result.missing) ? result.missing : [];
  const errorTokens = Array.isArray(result.errorTokens)
    ? result.errorTokens
    : [];

  const fields = resolutions.map((r) => {
    const entry = { token: r.token, path: r.path, state: r.state };
    if (r.text != null) entry.text = r.text;
    if (r.approximate) entry.approximate = true;
    if (r.viaItem) entry.viaItem = true;
    return entry;
  });

  const exclusions = missing.map((m) => ({
    token: m.token,
    reason: exclusionReason(m.state)
  }));

  const unresolved = Array.from(new Set(errorTokens.map((t) => String(t))));

  return {
    templateId: meta.templateId || undefined,
    templateVersion: meta.templateVersion,
    templateName: meta.templateName,
    familyId: meta.familyId,
    familyMemberId: meta.familyMemberId,
    letterId: meta.letterId || undefined,
    mergedValues: { fields },
    exclusions,
    unresolved
  };
}

/**
 * Persist a generation provenance record for the letter just exported.
 * @param {object} editor CKEditor instance carrying _lmGenerationMeta / _lmTemplateMeta
 * @param {object} result render result returned by resolveEditorContent()
 * @returns {Promise<object|null>} the created record, or null if skipped/failed
 */
function notify(editor, status) {
  const cb = editor && editor._lmOnGenerationRecorded;
  if (typeof cb === 'function') {
    try {
      cb(status);
    } catch (_e) {
      // a broken listener must not affect the export
    }
  }
}

export async function recordGeneration(editor, result) {
  if (!editor || !result) return null;

  const meta = editor._lmGenerationMeta;
  if (!meta || !meta.enabled || !meta.familyMemberId) return null;

  const tpl = editor._lmTemplateMeta || {};

  try {
    const payload = buildGenerationPayload(
      {
        templateId: tpl.templateId,
        templateVersion: tpl.templateVersion,
        templateName: tpl.templateName,
        familyId: meta.familyId,
        familyMemberId: meta.familyMemberId,
        letterId: meta.letterId
      },
      result
    );
    const { data } = await axios.post(
      APICONSTANTS.recordLetterGeneration,
      payload
    );
    // Surface success/failure so the audit write can never be lost silently
    // (medicolegally load-bearing, brief §14.2).
    notify(editor, { ok: true, id: data && data.id });
    return data;
  } catch (_e) {
    notify(editor, { ok: false });
    return null;
  }
}
